import { motion } from "framer-motion";
import logoUrl from "@assets/logo.png";

interface BrandLogoProps {
  size?: "sm" | "md" | "lg" | "xl" | "2xl";
  showText?: boolean;
  className?: string;
  animated?: boolean;
}

const sizes = {
  sm: { img: "h-10", text: "text-lg" },
  md: { img: "h-12", text: "text-xl" },
  lg: { img: "h-16", text: "text-2xl" },
  xl: { img: "h-20", text: "text-3xl" },
  "2xl": { img: "h-24", text: "text-4xl" },
};

export function BrandLogo({ size = "md", showText = true, className = "", animated = false }: BrandLogoProps) {
  const sizeConfig = sizes[size];
  
  if (!animated) {
    // Static mark: mascot only (the full logo.png already carries the wordmark) + plain two-tone wordmark.
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        {!showText && <img src="/favicon.png" alt="FillSign" className={`${sizeConfig.img} object-contain`} />}
        {showText && (
          <span className={`font-semibold tracking-tight ${sizeConfig.text}`}>
            Fill<span className="text-[#4A6D2E] dark:text-[#A5D66F]">Sign</span>
          </span>
        )}
      </div>
    );
  }
  
  return (
    <motion.div 
      className={`flex items-center gap-3 ${className}`}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      {/* Animated logo */}
      <motion.div className="relative">
        {/* Glow effect behind logo */}
        <motion.div
          className="absolute inset-0 bg-gradient-to-r from-green-400/30 via-emerald-400/30 to-teal-400/30 rounded-full blur-xl"
          animate={{
            scale: [1, 1.15, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        
        {/* Main logo with gentle float animation */}
        <motion.img 
          src={logoUrl} 
          alt="FillSign" 
          className={`${sizeConfig.img} object-contain relative z-10 drop-shadow-lg`}
          animate={{
            y: [0, -3, 0],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      </motion.div>
      
      {/* Animated Text */}
      {showText && (
        <motion.div className="relative">
          <motion.span 
            className={`font-bold ${sizeConfig.text} bg-gradient-to-r from-green-600 via-emerald-500 to-teal-500 bg-clip-text text-transparent`}
            style={{
              backgroundSize: "200% 200%",
            }}
          >
            FillSign
          </motion.span>
          
          {/* Underline animation */}
          <motion.div
            className="absolute -bottom-1 left-0 h-0.5 bg-gradient-to-r from-green-500 via-emerald-400 to-teal-500 rounded-full"
            initial={{ width: "0%" }}
            animate={{ width: ["0%", "100%", "0%"] }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        </motion.div>
      )}
    </motion.div>
  );
}

// Hero Logo - Extra large animated version for landing page
export function HeroBrandLogo({ className = "" }: { className?: string }) {
  return (
    <motion.div 
      className={`flex flex-col items-center gap-4 ${className}`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8 }}
    >
      {/* Large animated logo */}
      <motion.div className="relative">
        {/* Glow layer */}
        <motion.div
          className="absolute inset-[-15px] bg-gradient-to-r from-green-400/25 via-emerald-400/25 to-teal-400/25 rounded-full blur-2xl"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{ duration: 4, repeat: Infinity }}
        />
        
        {/* Main logo */}
        <motion.img 
          src={logoUrl} 
          alt="FillSign" 
          className="h-32 w-auto object-contain relative z-10 drop-shadow-2xl"
          animate={{
            y: [0, -6, 0],
          }}
          transition={{
            duration: 5,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      </motion.div>
    </motion.div>
  );
}

export function BrandIcon({ size = "md", className = "", animated = false }: Omit<BrandLogoProps, "showText">) {
  const sizeConfig = sizes[size];
  
  if (animated) {
    return (
      <motion.img 
        src={logoUrl} 
        alt="FillSign" 
        className={`${sizeConfig.img} object-contain ${className}`}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      />
    );
  }
  
  return (
    <img src={logoUrl} alt="FillSign" className={`${sizeConfig.img} object-contain ${className}`} />
  );
}
